import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, CalendarDays, Users, Trophy, CreditCard, Vote, Gift, Shield, MoreHorizontal, LogOut, UserCircle2, ShoppingBag, History } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Avatar, Logo } from "./common";
import { NotificationBell } from "./NotificationBell";

const NAV = [
  { to: "/app", label: "Início", icon: Home, end: true },
  { to: "/app/etapas", label: "Etapas", icon: CalendarDays },
  { to: "/app/atletas", label: "Atletas", icon: Users },
  { to: "/app/ranking", label: "Ranking", icon: Trophy },
  { to: "/app/cobrancas", label: "Cobranças", icon: CreditCard },
  { to: "/app/votacao", label: "Votação", icon: Vote },
  { to: "/app/brindes", label: "Brindes", icon: Gift },
  { to: "/app/loja", label: "Loja", icon: ShoppingBag },
  { to: "/app/temporadas", label: "Temporadas", icon: History },
];
const BOTTOM = [NAV[0], NAV[1], NAV[4], NAV[3], { to: "/app/mais", label: "Mais", icon: MoreHorizontal }];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-[background-color,color] ${
    isActive ? "bg-[#E4F222] text-black" : "text-[#94A3B8] hover:bg-[#1A202E] hover:text-white"
  }`;

const Sidebar = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-[#232B3E] bg-[#0B0E14] p-5 md:flex">
      <div className="mb-8 px-2"><Logo where="app" max={140} /></div>
      <nav className="flex-1 space-y-1">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`sidebar-${n.label.toLowerCase()}`}>
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
        <NavLink to={`/app/atletas/${user.user_id}`} className={linkCls} data-testid="sidebar-perfil">
          <UserCircle2 className="h-4 w-4" /> Meu perfil
        </NavLink>
        {isAdmin && (
          <NavLink to="/app/admin" className={linkCls} data-testid="sidebar-admin">
            <Shield className="h-4 w-4" /> Administração
          </NavLink>
        )}
      </nav>
      <div className="card-x flex items-center gap-3 p-3">
        <Avatar user={user} size={36} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{user.name}</div>
          <div className="text-[11px] uppercase tracking-wider text-[#94A3B8]">{isAdmin ? "Admin" : "Atleta"}</div>
        </div>
        <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="text-[#94A3B8] hover:text-white">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
};

const MobileTop = () => {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#232B3E] bg-[#0B0E14]/90 px-4 py-3 backdrop-blur-xl md:hidden">
      <Logo where="app" max={64} />
      <div className="flex items-center gap-2">
        <NotificationBell />
        <NavLink to={`/app/atletas/${user.user_id}`} data-testid="mobile-profile-link"><Avatar user={user} size={34} /></NavLink>
      </div>
    </header>
  );
};

const DesktopTop = () => {
  const { user } = useAuth();
  return (
    <div className="fixed right-8 top-5 z-30 hidden items-center gap-3 md:flex">
      <NotificationBell />
      <NavLink to={`/app/atletas/${user.user_id}`} data-testid="desktop-profile-link"><Avatar user={user} size={38} /></NavLink>
    </div>
  );
};

const BottomNav = () => (
  <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-[#232B3E] bg-[#0B0E14]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden">
    {BOTTOM.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.label.toLowerCase()}`}
        className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold ${isActive ? "text-[#E4F222]" : "text-[#64748B]"}`}>
        <n.icon className="h-5 w-5" /> {n.label}
      </NavLink>
    ))}
  </nav>
);

export default function AppShell() {
  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Sidebar />
      <MobileTop />
      <DesktopTop />
      <main className="px-4 pb-28 pt-6 sm:px-6 md:ml-64 md:px-10 md:pb-12 md:pt-20">
        <div className="mx-auto max-w-6xl"><Outlet /></div>
      </main>
      <BottomNav />
    </div>
  );
}
